import React, { useState } from "react";
import { Link } from "react-router-dom";
import { HandHeart, EnvelopeSimple, MapPin, ArrowRight } from "@phosphor-icons/react";
import axios from "axios";
import { toast } from "sonner";
import { SITE, NAV, API_BASE } from "../lib/site";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/newsletter`, { email }, { withCredentials: false });
      toast.success("Thank you — you're subscribed to ministry updates.");
      setEmail("");
    } catch (err) {
      toast.error("We couldn't subscribe you right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-softblue border-t border-ink-100" data-testid="site-footer">
      <div className="container-x py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Link to="/" className="inline-flex items-center gap-2.5">
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-primary/10 text-primary">
                <HandHeart size={24} weight="duotone" />
              </span>
              <span className="leading-tight">
                <span className="block font-heading text-2xl text-ink-900 font-medium">
                  Begin Again
                </span>
                <span className="block text-[10px] uppercase tracking-[0.22em] text-primary font-semibold">
                  Living Ministries
                </span>
              </span>
            </Link>
            <p className="mt-6 text-ink-700 leading-relaxed max-w-md">
              Faith-based encouragement, grief support, and spiritual care for
              individuals, families, and caregivers walking through life's
              hardest seasons.
            </p>

            <form onSubmit={subscribe} className="mt-8 max-w-md" data-testid="footer-newsletter-form">
              <label className="block text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-3">
                Receive gentle encouragement
              </label>
              <div className="flex items-center gap-2 bg-white border border-ink-200 rounded-full p-1.5 pl-5 shadow-sm">
                <EnvelopeSimple size={18} className="text-ink-500" />
                <input
                  type="email"
                  required
                  placeholder="Your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="footer-newsletter-email"
                  className="flex-1 bg-transparent outline-none text-sm text-ink-800 placeholder:text-ink-500"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="footer-newsletter-submit"
                  className="inline-flex items-center gap-1.5 bg-primary text-white text-sm font-semibold rounded-full px-4 py-2.5 hover:bg-primary-dark transition-colors disabled:opacity-60"
                >
                  {loading ? "..." : "Subscribe"}
                  <ArrowRight size={14} weight="bold" />
                </button>
              </div>
            </form>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-sm font-semibold uppercase tracking-[0.2em] text-primary mb-5">
              Explore
            </h4>
            <ul className="space-y-3">
              {NAV.map((n) => (
                <li key={n.to}>
                  <Link
                    to={n.to}
                    data-testid={`footer-link-${n.to.replace(/\//g, "") || "home"}`}
                    className="text-ink-700 hover:text-primary transition-colors"
                  >
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <h4 className="text-sm font-semibold uppercase tracking-[0.2em] text-primary mb-5">
              Stay connected
            </h4>
            <div className="space-y-4 text-ink-700">
              <a
                href={`mailto:${SITE.email}`}
                className="flex items-start gap-3 hover:text-primary transition-colors break-all"
                data-testid="footer-email"
              >
                <EnvelopeSimple size={18} weight="duotone" className="text-primary mt-0.5 shrink-0" />
                <span className="leading-snug">{SITE.email}</span>
              </a>
              <address className="flex items-start gap-3 not-italic" data-testid="footer-address">
                <MapPin size={18} weight="duotone" className="text-primary mt-0.5 shrink-0" />
                <span className="leading-snug">
                  {SITE.name}
                  <br />
                  {SITE.address.line1}
                  <br />
                  {SITE.address.line2}
                </span>
              </address>
            </div>

            <Link
              to="/donate"
              data-testid="footer-donate-cta"
              className="mt-8 inline-flex items-center gap-2 bg-secondary text-white rounded-full px-6 py-3 font-semibold hover:bg-secondary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
            >
              <HandHeart size={16} weight="fill" />
              Partner With the Ministry
            </Link>
          </div>
        </div>

        <div className="section-divider my-12" />

        <div className="text-sm text-ink-500 leading-relaxed space-y-3">
          <p data-testid="footer-disclaimer" className="italic max-w-4xl">
            {SITE.disclaimer}
          </p>
          <p data-testid="footer-501c3" className="max-w-4xl">
            {SITE.name} is a registered 501(c)(3) nonprofit organization dedicated to providing
            faith-based encouragement, grief support, spiritual care resources, and community support.
          </p>
          <p>
            &copy; {new Date().getFullYear()} {SITE.name}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
